import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { DashboardSummary, RouterDto, RouterStatus } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { useCurrentRouter } from '../lib/current-router';
import { THEME_CYCLE, THEME_ICON, THEME_LABEL, useTheme } from '../lib/theme';
import { RoleBadge } from './status';

interface NavItem {
  to: string;
  label: string;
  adminOnly?: boolean;
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Operación',
    items: [
      { to: '/', label: 'Dashboard' },
      { to: '/hotspot-users', label: 'Usuarios HotSpot' },
      { to: '/vouchers', label: 'Vouchers' },
      { to: '/pppoe-clients', label: 'Clientes PPPoE' },
      { to: '/sessions', label: 'Sesiones' },
      { to: '/performance', label: 'Horas pico' },
      { to: '/my-billing', label: 'Mi deuda' },
    ],
  },
  {
    section: 'Configuración',
    items: [
      { to: '/network', label: 'Mapa de red', adminOnly: true },
      { to: '/routers', label: 'Routers', adminOnly: true },
      { to: '/plans', label: 'Planes', adminOnly: true },
      { to: '/panel-users', label: 'Usuarios del panel', adminOnly: true },
      { to: '/billing', label: 'Facturación', adminOnly: true },
      { to: '/audit', label: 'Auditoría', adminOnly: true },
      { to: '/health', label: 'Salud del sistema', adminOnly: true },
    ],
  },
];

const STATUS_META: Record<RouterStatus, { label: string; tone: string }> = {
  ONLINE: { label: 'en línea', tone: 'ok' },
  OFFLINE: { label: 'sin conexión', tone: 'bad' },
  AUTH_ERROR: { label: 'credencial rechazada', tone: 'bad' },
  UNKNOWN: { label: 'sin sondear', tone: 'neutral' },
};

/** Chip de la barra superior: el estado del router siempre a la vista. */
function RouterChip({ router }: { router: RouterDto }) {
  const meta = STATUS_META[router.status];
  return (
    <Link
      to="/routers"
      className={`chip ${meta.tone}`}
      title={`${router.name} · ${meta.label}${router.lastError ? ` — ${router.lastError}` : ''}`}
    >
      <span className="chip-dot" />
      <strong>{router.identity ?? router.name}</strong>
      <span className="chip-status">{meta.label}</span>
    </Link>
  );
}

export function Layout() {
  const { profile, logout, hasRole } = useAuth();
  const [theme, setTheme] = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const current = useCurrentRouter();

  // Se reutiliza el resumen del dashboard para los avisos: ya está en caché desde la
  // pantalla principal, así que no cuesta una petición extra. Es el del router de
  // trabajo, igual que el resto de pantallas.
  const summary = useQuery({
    queryKey: ['dashboard', 'summary', current.routerId],
    queryFn: () =>
      api.get<DashboardSummary>('/dashboard/summary', { routerId: current.routerId || undefined }),
    enabled: !current.loading,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // La barra superior existe para responder «¿está vivo el MikroTik?» sin navegar,
  // así que se refresca con más frecuencia que el resto.
  const routers = useQuery({
    queryKey: ['routers'],
    queryFn: () => api.get<RouterDto[]>('/routers'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const offline = summary.data?.routersNeedingAttention.length ?? 0;
  const pendingSync = summary.data?.hotspotUsers.pendingSync ?? 0;
  const pendingPppoe = summary.data?.pppoeClients?.pendingSync ?? 0;
  const activeSessions = summary.data?.activeSessions.count ?? 0;

  return (
    <div className="app-shell">
      <nav className={drawerOpen ? 'sidebar open' : 'sidebar'}>
        <div className="sidebar-brand">
          <span className="brand-mark">M</span>
          MikroManager
        </div>

        {NAV.map((group) => {
          const items = group.items.filter(
            (item) => !item.adminOnly || hasRole('SUPER_ADMIN'),
          );
          if (items.length === 0) return null;

          return (
            <div key={group.section}>
              <div className="sidebar-section">{group.section}</div>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                  {item.to === '/routers' && offline > 0 && <span className="badge bad">{offline}</span>}
                  {item.to === '/hotspot-users' && pendingSync > 0 && (
                    <span className="badge bad">{pendingSync}</span>
                  )}
                  {item.to === '/pppoe-clients' && pendingPppoe > 0 && (
                    <span className="badge bad">{pendingPppoe}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}

        <div className="sidebar-footer">
          <div style={{ padding: '4px 8px' }}>
            <div style={{ fontWeight: 600 }}>{profile?.name}</div>
            <div className="faint" style={{ fontSize: 12, marginBottom: 6 }}>
              {profile?.email}
            </div>
            {profile && <RoleBadge role={profile.role} />}
          </div>
          <button type="button" className="ghost small" style={{ width: '100%', marginTop: 8 }} onClick={() => void logout()}>
            Cerrar sesión
          </button>
        </div>
      </nav>

      {drawerOpen && (
        <button
          type="button"
          className="drawer-backdrop"
          aria-label="Cerrar menú"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="menu-button"
            aria-label="Abrir menú"
            onClick={() => setDrawerOpen(true)}
          >
            ☰
          </button>

          <div className="topbar-chips">
            {current.routers.length > 0 && (
              <select
                value={current.routerId}
                onChange={(event) => current.setRouterId(event.target.value)}
                aria-label="Router de trabajo"
                title="Router de trabajo: todas las pantallas muestran sólo sus datos"
                style={{ width: 'auto' }}
              >
                {current.routers.map((router) => (
                  <option key={router.id} value={router.id}>
                    {router.name}
                  </option>
                ))}
              </select>
            )}
            {(routers.data ?? []).map((router) => (
              <RouterChip key={router.id} router={router} />
            ))}
            {routers.data && routers.data.length === 0 && (
              <Link to="/routers" className="chip neutral">
                <span className="chip-dot" />
                Sin routers — añade el primero
              </Link>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link to="/sessions" className="topbar-sessions" title="Sesiones activas ahora">
              <span className={activeSessions > 0 ? 'chip-dot ok-dot' : 'chip-dot'} />
              {activeSessions} {activeSessions === 1 ? 'sesión activa' : 'sesiones activas'}
            </Link>
            <button
              type="button"
              className="ghost small"
              title={THEME_LABEL[theme]}
              aria-label={THEME_LABEL[theme]}
              onClick={() => setTheme(THEME_CYCLE[theme])}
            >
              {THEME_ICON[theme]}
            </button>
          </div>
        </header>

        <Outlet />
      </div>
    </div>
  );
}
